import React, { useState, useEffect, useCallback } from "react";
import {
  Table, TableBody, TableCell, TableHead, TableRow,
  Paper, Button, Typography, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, MenuItem, CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import HeadsetMicIcon from "@material-ui/icons/HeadsetMic";
import { toast } from "react-toastify";

import { listMyTickets, createTicket } from "../../services/supportTicketService";
import SupportTicketChat from "../../components/SupportTicketChat";
import TableRowSkeleton from "../../components/TableRowSkeleton";

const PRIORITY_STYLES = {
  baixa: { background: "#dcfce7", color: "#166534" },
  media: { background: "#fef9c3", color: "#92400e" },
  alta: { background: "#fed7aa", color: "#9a3412" },
  urgente: { background: "#fee2e2", color: "#991b1b" },
};

const STATUS_STYLES = {
  aberto: { background: "#e0f2fe", color: "#0369a1" },
  em_atendimento: { background: "#f3e8ff", color: "#7c3aed" },
  aguardando_resposta: { background: "#fef9c3", color: "#92400e" },
  resolvido: { background: "#dcfce7", color: "#166534" },
  fechado: { background: "#f1f5f9", color: "#64748b" },
};

const STATUS_LABELS = {
  aberto: "Aberto", em_atendimento: "Em atendimento",
  aguardando_resposta: "Aguardando resposta", resolvido: "Resolvido", fechado: "Fechado",
};

const CATEGORY_LABELS = {
  tecnico: "Técnico", financeiro: "Financeiro", conta: "Conta",
  funcionalidade: "Funcionalidade", outro: "Outro",
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    width: "100%",
    background: "#f8fafc",
    minHeight: "100vh",
  },
  pageHeader: { marginBottom: theme.spacing(3) },
  pageTitle: { fontSize: "28px", fontWeight: 700, color: "#1a1a2e", marginBottom: theme.spacing(0.5) },
  pageSubtitle: { fontSize: "14px", color: "#6b7280" },
  toolbarRow: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    background: "#fff", borderRadius: 12, padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(3), boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    flexWrap: "wrap", gap: 8,
  },
  content: { background: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.04)", overflow: "hidden" },
  tableHead: {
    "& th": {
      backgroundColor: "var(--sidebar-color, #1e293b)", color: "#fff",
      fontWeight: 600, fontSize: "0.8rem", textTransform: "uppercase",
      letterSpacing: "0.04em", padding: "10px 16px",
    },
  },
  badge: { padding: "2px 10px", borderRadius: 12, fontSize: "0.72rem", fontWeight: 700 },
  emptyBox: { textAlign: "center", padding: "60px 0" },
  emptyIcon: { fontSize: 56, color: "#cbd5e1", marginBottom: 12 },
}));

const formatDate = (d) => new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const SupportTickets = () => {
  const classes = useStyles();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [chatTicketId, setChatTicketId] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", category: "outro", priority: "media" });
  const [creating, setCreating] = useState(false);

  const sidebarColor = (() => {
    try { return window.__MUI_THEME__?.palette?.primary?.main || "#1e293b"; } catch { return "#1e293b"; }
  })();

  const load = useCallback(() => {
    setLoading(true);
    listMyTickets()
      .then(({ data }) => setTickets(data))
      .catch(() => toast.error("Erro ao carregar chamados"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleField = (field) => (e) => { const v = e.target.value; setForm((p) => ({ ...p, [field]: v })); };

  const handleCreate = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Preencha título e descrição"); return;
    }
    setCreating(true);
    try {
      await createTicket(form);
      toast.success("Chamado aberto com sucesso!");
      setNewOpen(false);
      setForm({ title: "", description: "", category: "outro", priority: "media" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erro ao abrir chamado");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className={classes.root} style={{ "--sidebar-color": sidebarColor }}>
      {/* Modal novo chamado */}
      <Dialog open={newOpen} onClose={() => setNewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Abrir chamado de suporte</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <TextField label="Título *" fullWidth variant="outlined" size="small"
                value={form.title} onChange={handleField("title")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Categoria" select fullWidth variant="outlined" size="small"
                value={form.category} onChange={handleField("category")}>
                <MenuItem value="tecnico">Técnico</MenuItem>
                <MenuItem value="financeiro">Financeiro</MenuItem>
                <MenuItem value="conta">Conta</MenuItem>
                <MenuItem value="funcionalidade">Funcionalidade</MenuItem>
                <MenuItem value="outro">Outro</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Prioridade" select fullWidth variant="outlined" size="small"
                value={form.priority} onChange={handleField("priority")}>
                <MenuItem value="baixa">Baixa</MenuItem>
                <MenuItem value="media">Média</MenuItem>
                <MenuItem value="alta">Alta</MenuItem>
                <MenuItem value="urgente">Urgente</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Descreva o problema *" fullWidth multiline rows={5}
                variant="outlined" size="small" value={form.description}
                onChange={handleField("description")}
                placeholder="Descreva com detalhes o que está acontecendo, passos para reproduzir o problema, mensagens de erro, etc." />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)} disabled={creating}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} /> : "Abrir chamado"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chat */}
      <SupportTicketChat
        open={Boolean(chatTicketId)}
        onClose={() => setChatTicketId(null)}
        ticketId={chatTicketId}
        isAdmin={false}
        onUpdated={load}
      />

      <div className={classes.pageHeader}>
        <Typography className={classes.pageTitle}>Chamados de Suporte</Typography>
        <Typography className={classes.pageSubtitle}>
          Abra chamados e acompanhe o atendimento da nossa equipe de suporte
        </Typography>
      </div>

      <div className={classes.toolbarRow}>
        <Typography variant="body2" style={{ color: "#64748b" }}>
          {tickets.filter(t => t.status !== "fechado").length} chamado(s) ativo(s)
        </Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />}
          style={{ textTransform: "none", borderRadius: 8, fontWeight: 600 }}
          onClick={() => setNewOpen(true)}>
          Novo chamado
        </Button>
      </div>

      <Paper className={classes.content}>
        <Table size="small">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Prioridade</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Atualizado</TableCell>
              <TableCell align="center">Abrir</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={7} />
            ) : tickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <div className={classes.emptyBox}>
                    <HeadsetMicIcon className={classes.emptyIcon} />
                    <Typography style={{ color: "#64748b", fontWeight: 600 }}>
                      Nenhum chamado aberto
                    </Typography>
                    <Typography variant="body2" style={{ color: "#94a3b8", marginTop: 4 }}>
                      Clique em "Novo chamado" para solicitar suporte
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              tickets.map((t) => (
                <TableRow key={t.id} hover style={{ cursor: "pointer" }} onClick={() => setChatTicketId(t.id)}>
                  <TableCell style={{ color: "#94a3b8", fontSize: "0.8rem" }}>#{t.id}</TableCell>
                  <TableCell>
                    <Typography style={{ fontWeight: 600, fontSize: "0.875rem" }}>{t.title}</Typography>
                  </TableCell>
                  <TableCell>
                    <span style={{ color: "#64748b", fontSize: "0.8rem" }}>
                      {CATEGORY_LABELS[t.category] || t.category}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={classes.badge} style={PRIORITY_STYLES[t.priority] || {}}>
                      {t.priority}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={classes.badge} style={STATUS_STYLES[t.status] || {}}>
                      {STATUS_LABELS[t.status] || t.status}
                    </span>
                  </TableCell>
                  <TableCell style={{ fontSize: "0.8rem", color: "#64748b" }}>
                    {formatDate(t.updatedAt)}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); setChatTicketId(t.id); }}>
                      <ChatBubbleOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </div>
  );
};

export default SupportTickets;
